import { filterDOMProps } from '@react-aria/utils';
import type { AriaLabelingProps } from '@react-types/shared';
import type { ReactNode } from 'react';
import { createElement, forwardRef } from 'react';

import type {
  BaseStyleProps,
  BoxStyleProps,
  UnsafeStyleProps,
} from '@infinex/ui-styles';
import { useStyleProps } from '@infinex/ui-styles';

import type { HTMLTag, PrimitiveValues } from './types';

export type ViewProps = {
  /** The content of the element. */
  children?: ReactNode;
  /**
   * The HTML tag used to render the element.
   * @default 'div'
   */
  elementType?: HTMLTag;
  /**
   * The `id` attribute defines an identifier (ID) that must be unique
   * within the entire document.
   */
  id?: string;
  /**
   * The `role` attribute provides semantic meaning to content, allowing screen
   * readers and other tools to present and support interaction with an object
   * in a way that is consistent with user expectations.
   */
  role?: string;
} & AriaLabelingProps &
  BaseStyleProps &
  BoxStyleProps &
  UnsafeStyleProps;

/**
 * The `View` is a primitive component for composing layouts.
 */
export const View = forwardRef<HTMLElement, ViewProps>(
  function View(props, ref) {
    const { children, elementType = 'div', role } = props;
    const { style, className } = useStyleProps(props);

    return createElement(elementType, {
      ...filterDOMProps(props, { labelable: true }),
      children,
      className,
      ref,
      role,
      style,
    });
  }
);

/**
 * @private Mock component to improve Storybook’s `argTypes` behaviour. By
 * unwrapping the `Responsive<T>` object types we allow the controls to observe
 * primitive values only.
 *
 * NOTE: Must be exported, doesn’t work inline from the stories file.
 */
export function StorybookArgTypesHack(props: PrimitiveValues<ViewProps>) {
  return <View {...props} />;
}
